<template>
  <Section
    v-bind="$attrs"
    :title="title"
    :shouldShowSearch="shouldShowSearch"
    :toolbarActions="toolbarActions"
    @onClicked="(val) => $emit(val)"
    :isPrimary="isPrimary"
    icon="construction"
    :isLoading="isLoading"
    @onShouldSearch="(val) => $emit('onShouldSearch', val)"
  >
    <template v-if="$slots.toolbarExtension" v-slot:toolbarExtension>
      <slot name="toolbarExtension" />
    </template>
    <v-layout column>
      <v-flex>
        <v-data-table
          :headers="headers"
          :items="assetList"
          item-key="id"
          :select-all="shouldShowCheckBoxes"
          :hide-actions="true"
          class="elevation-1"
        >
          <template v-slot:headers="props">
            <tr>
              <th v-if="shouldShowCheckBoxes">
                <v-checkbox
                  :input-value="areAllSelected"
                  :indeterminate="areSomeSelected && !areAllSelected"
                  readonly
                  primary
                  hide-details
                  @click.native="!isLoading ? toggleAll(props.all) : ''"
                  :disabled="isLoading"
                ></v-checkbox>
              </th>
              <th
                class="column text-xs-left"
                v-for="header in props.headers"
                :key="header.text"
              >
                {{ header.text }}
              </th>
            </tr>
          </template>
          <template v-slot:items="props">
            <td v-if="shouldShowCheckBoxes">
              <v-checkbox
                :input-value="selectedItems.some((i) => i.id === props.item.id)"
                @change="handleItemSelect(props.item)"
                primary
                hide-details
                :disabled="isLoading"
              ></v-checkbox>
            </td>
            <td class="text-xs-left">
              {{
                _entityPropertyValueByName({
                  entityList: assetStatusList,
                  idKey: "id",
                  idValue: props.item.statusId,
                  propertyKey: "name",
                })
              }}
              <br />
              {{
                _formatJavascriptDate({
                  date: props.item.dateTimeStatusLastUpdated,
                })
              }}
            </td>
            <td class="text-xs-left">
              {{
                _entityPropertyValueByName({
                  entityList: locationList,
                  idKey: "id",
                  idValue: props.item.locationId,
                  propertyKey: "name",
                })
              }}
              <br />
              {{
                _formatJavascriptDate({
                  date: props.item.dateTimeLocationLastUpdated,
                })
              }}
            </td>
            <td class="text-xs-left">
              {{ props.item.name }}
            </td>
            <td class="text-xs-left">
              {{
                _entityPropertyValueByName({
                  entityList: modelList,
                  idKey: "id",
                  idValue: props.item.modelId,
                  propertyKey: "name",
                })
              }}
            </td>
            <td class="text-xs-left">
              {{
                _manufacturerByModelId({
                  modelList: modelList,
                  manufacturerList: manufacturerList,
                  modelId: props.item.modelId,
                }).name || "[UNKNOWN]"
              }}
            </td>
            <td class="text-xs-left">
              {{
                _categoryByModelId({
                  modelList: modelList,
                  categoryList: categoryList,
                  modelId: props.item.modelId,
                }).name || "[UNKNOWN]"
              }}
            </td>
            <td class="text-xs-left">
              {{
                _entityPropertyValueByName({
                  entityList: clientList,
                  idKey: "id",
                  idValue: props.item.clientId,
                  propertyKey: "name",
                })
              }}
            </td>
            <td class="text-xs-left">
              {{
                _entityPropertyValueByName({
                  entityList: activityTypeList,
                  idKey: "id",
                  idValue: props.item.activityTypeIdLast,
                  propertyKey: "name",
                })
              }}
              <br />
              {{
                _formatJavascriptDate({
                  date: props.item.dateTimeActivityLastPerformed,
                })
              }}
            </td>
            <td class="text-xs-right">
              <ControlButton
                text="View"
                depressed
                :disabled="isLoading"
                @onClicked="
                  $emit('onClickViewEquipment', { assetId: props.item.id })
                "
              />
            </td>
          </template>
        </v-data-table>
      </v-flex>
      <v-flex class="text-xs-center" py-3 v-if="currentPage > 0">
        <v-pagination
          py-4
          :value="currentPage"
          @input="(val) => $emit('onClickPageChange', val)"
          :length="numberOfPages"
          :total-visible="7"
          :disabled="isLoading"
        ></v-pagination>
      </v-flex>
    </v-layout>
  </Section>
</template>

<script>
import mixinAssetHelper from '../../../../common/mixins/mixinAssetHelper'

import Section from '../../../../common/components/layouts/Section'
import ControlButton from '../../../../common/components/controls/ControlButton'

export default {
  inheritAttrs: false,
  components: {
    Section,
    ControlButton,
  },
  mixins: [mixinAssetHelper],
  props: {
    toolbarActions: {
      model: Array,
      validator: (val) => {
        return val.every((action) => action.hasOwnProperty('emitEvent'))
      },
    },
    value: {
      type: Array,
      default: () => [],
    },
    currentPage: {
      type: Number,
      default: -1,
    },
    numberOfPages: {
      type: Number,
    },
    assetList: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
    },
    isPrimary: {
      type: Boolean,
      default: true,
    },
    shouldShowSearch: {
      type: Boolean,
    },
    shouldShowCheckBoxes: {
      type: Boolean,
      default: true,
    },
    modelList: {
      type: Array,
      required: true,
    },
    manufacturerList: {
      type: Array,
      required: true,
    },
    categoryList: {
      type: Array,
      required: true,
    },
    activityTypeList: {
      type: Array,
      required: true,
    },
    assetStatusList: {
      type: Array,
      required: true,
    },
    locationList: {
      type: Array,
      required: true,
    },
    clientList: {
      type: Array,
      required: true,
    },
    userList: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    headers: [
      {
        text: 'Status',
        value: 'activityTypeIdStatus',
        align: 'left',
        sortable: false,
      },
      { text: 'Location', value: 'locationId', align: 'left', sortable: false },
      { text: 'Serial/Id', value: 'name', align: 'left', sortable: false },
      { text: 'Model', value: 'modelId', sortable: false },
      {
        text: 'Manufacturer',
        value: 'manufacturerId',
        align: 'left',
        sortable: false,
      },
      { text: 'Category', value: 'categoryId', align: 'left', sortable: false },
      { text: 'Owner', value: 'clientId', align: 'left', sortable: false },
      {
        text: 'Last Activity',
        value: 'activityTypeIdLast',
        align: 'left',
        sortable: false,
      },
      { text: '', sortable: false },
    ],
    selectedItems: [],
  }),
  computed: {
    areSomeSelected() {
      if (!this.selectedItems || !this.selectedItems.length) {
        return false
      }
      const assetIds = this.assetList.map((asset) => asset.id)
      for (let index = 0; index < assetIds.length; index++) {
        const assetId = assetIds[index]
        const isIdSelected = this.selectedItems.some(
          (selectedItem) => selectedItem.id === assetId
        )
        if (isIdSelected) {
          return true
        }
      }
      return false
    },
    areAllSelected() {
      if (!this.selectedItems || !this.selectedItems.length) {
        return false
      }
      const assetIds = this.assetList.map((asset) => asset.id)
      for (let index = 0; index < assetIds.length; index++) {
        const assetId = assetIds[index]
        const isIdSelected = this.selectedItems.some(
          (selectedItem) => selectedItem.id === assetId
        )
        if (!isIdSelected) {
          return false
        }
      }
      return true
    },
  },
  watch: {
    value() {
      this.selectedItems = this.value
    },
  },
  methods: {
    handleItemSelect(item) {
      const index = this.selectedItems.map((i) => i.id).indexOf(item.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(item)
      }
      this.$emit('input', this.selectedItems)
    },
    toggleAll() {
      if (this.areAllSelected) {
        this.assetList.forEach((asset) => {
          const index = this.selectedItems.map((i) => i.id).indexOf(asset.id)
          if (index > -1) {
            this.selectedItems.splice(index, 1)
          }
        })
      } else {
        this.assetList.forEach((asset) => {
          const index = this.selectedItems.map((i) => i.id).indexOf(asset.id)
          if (index === -1) {
            this.selectedItems.push(asset)
          }
        })
      }
      this.$emit('input', this.selectedItems)
    },
  },
}
</script>
